<div class="flex flex-col gap-2 min-w-0">
   <div class="sticky top-0 z-20 bg-surface flex flex-col gap-2 pb-2">
      <div class="flex items-center gap-2 min-w-0">
         <h3 class="mat-title-medium flex-1 min-w-0 truncate">{{ 'approval.remarks' | translate }}</h3>

         <div
            *webvisAclRestrict="commentRestriction"
            class="flex gap-2"
         >
            <mat-checkbox
               (change)="commentMode.set($event.checked ? 'slide' : 'general')"
               [checked]="commentMode() === 'slide'"
               class="comment-checkbox"
               labelPosition="before"
            >
               {{ 'approval.comment_on_slide' | translate: { slide: currentSlide() } }}
            </mat-checkbox>

            <mat-divider
               [vertical]="true"
               class="h-8 mr-2"
            ></mat-divider>
         </div>

         <button
            [matMenuTriggerFor]="slideFilterMenu"
            class="h-8 shrink-0 rounded-lg border border-outline px-4 flex items-center gap-2 text-sm text-on-surface-variant"
         >
            @if (slideFilter() === 'all') {
               {{ 'approval.all_slides' | translate }}
            } @else if (slideFilter() === null) {
               {{ 'approval.general_comments' | translate }}
            } @else {
               {{ 'approval.slide' | translate }}: {{ slideFilter() }}
            }
            <fa-icon [icon]="['far', 'caret-down']" />
         </button>

         <mat-menu #slideFilterMenu="matMenu">
            <button
               (click)="setSlideFilter('all')"
               mat-menu-item
            >
               {{ 'approval.all_slides' | translate }}
            </button>
            @for (slide of uniqueSlideNumbers(); track slide) {
               <button
                  (click)="setSlideFilter(slide)"
                  mat-menu-item
               >
                  @if (slide) {
                     {{ 'approval.slide' | translate }}: {{ slide }}
                  } @else {
                     {{ 'approval.general_comments' | translate }}
                  }
               </button>
            }
         </mat-menu>
      </div>

      <div *webvisAclRestrict="commentRestriction">
         <div class="flex items-start gap-2 py-2">
            <shared-ui-text-input
               (keydown)="onCtrlEnter($event, 'comment')"
               [inputFormControl]="commentForm.controls.content"
               [label]="'approval.feedback_placeholder' | translate"
               class="flex-1"
               type="multi"
            />
         </div>

         <button
            (click)="submitComment()"
            [disabled]="commentForm.controls.content.value?.length < 3 || loading()"
            class="w-full"
            mat-flat-button
         >
            {{ 'approval.submit_comment' | translate }}
            <span class="ml-2 text-xs opacity-70 border border-current rounded-sm px-1">ctrl+enter</span>
         </button>
      </div>

      @if (loading() && !loadingReplyTo() && !loadingEditId() && !loadingDeleteId()) {
         <shared-ui-skeleton-loader
            [skeletonConfig]="{ appearance: { rows: 1, avatar: true, multiline: 3, divider: false }, styles: { width: '100%' } }"
            [type]="skeletonType"
            class="ml-5"
         />
      }
   </div>

   <div class="flex flex-col gap-4 mt-2">
      @for (comment of filteredComments(); track comment.id) {
         <div class="flex flex-col">
            <ng-container
               [ngTemplateOutlet]="commentTpl"
               [ngTemplateOutletContext]="{ $implicit: comment, isChild: false, hasChildren: !!comment.children?.length }"
            />

            @if (comment.children?.length) {
               <div class="flex flex-col gap-2 ml-13 mt-4">
                  @for (reply of comment.children; track reply.id) {
                     <ng-container
                        [ngTemplateOutlet]="commentTpl"
                        [ngTemplateOutletContext]="{ $implicit: reply, isChild: true, firstChild: $first, lastChild: $last }"
                     />
                  }
               </div>
            }
         </div>
      }
   </div>
</div>

<ng-template
   #commentTpl
   let-comment
   let-firstChild="firstChild"
   let-hasChildren="hasChildren"
   let-isChild="isChild"
   let-lastChild="lastChild"
>
   @if (loading() && (loadingEditId() === comment.id || loadingDeleteId() === comment.id)) {
      <shared-ui-skeleton-loader
         [class.ml-5]="!isChild"
         [skeletonConfig]="{ appearance: { rows: 1, avatar: true, multiline: 3, divider: false }, styles: { width: '100%' } }"
         [type]="skeletonType"
      />
   } @else {
      <div
         [attr.data-comment-id]="comment.id"
         [class.animate-highlight]="newCommentIds().has(comment.id)"
         class="grid grid-cols-[auto_1fr] gap-x-2 relative"
      >
         <div
            [class.child-after]="isChild && !lastChild"
            [class.child-before]="isChild && !firstChild"
            [class.first-child]="firstChild"
            [class.parent-after]="hasChildren"
            class="flex items-center gap-1 row-span-2 relative z-10"
         >
            <div
               [ngClass]="isChild ? 'ml-2' : 'w-18'"
               class="relative shrink-0 h-12 flex justify-end"
            >
               <shared-ui-initials-avatar [name]="comment.commentor?.full_name" />
               @if (comment.slide_number) {
                  <button
                     (click)="onSlideClick(comment.slide_number)"
                     class="absolute left-0 top-4 bg-extendedWarningContainer border border-extendedWarning rounded-full px-1 text-[9px] font-medium text-extendedOnWarningContainer"
                  >
                     {{ 'approval.slide' | translate }}: {{ comment.slide_number }}
                  </button>
               }
            </div>
         </div>
         <div class="flex-1 flex items-center z-10">
            @if (editingCommentId() === comment.id) {
               <div class="flex flex-col gap-2 w-full py-1">
                  <shared-ui-text-input
                     #editInput
                     (keydown)="onCtrlEnter($event, 'edit', comment.id)"
                     (keydown.escape)="cancelEdit()"
                     [inputFormControl]="editForm"
                     [placeholder]="'approval.feedback_placeholder' | translate"
                     class="flex-1"
                     type="multi"
                  />
                  <div class="flex gap-2">
                     <button
                        (click)="submitEdit(comment.id)"
                        [disabled]="editForm.value?.length < 3"
                        class="grow"
                        mat-flat-button
                     >
                        {{ 'general.save' | translate }}
                        <span class="ml-2 text-xs opacity-70 border border-current rounded-sm px-1">ctrl+enter</span>
                     </button>
                     <button
                        (click)="cancelEdit()"
                        mat-button
                     >
                        {{ 'general.cancel' | translate }}
                     </button>
                  </div>
               </div>
            } @else if (comment.isTombstone) {
               <p class="text-sm leading-5 italic text-on-surface-variant">{{ 'approval.comment_deleted' | translate }}</p>
            } @else {
               <p class="text-sm leading-5 whitespace-pre-line">{{ comment.content }}</p>
            }
         </div>
         <div class="col-start-2 flex items-center pr-11 text-[9px] z-10">
            @if (editingCommentId() !== comment.id && !comment.isTombstone) {
               <span class="text-outline font-medium">{{ comment.create_date_time | customDate: 'dateTime' }}</span>
            }
            @if (!comment.isTombstone) {
               <span class="flex-1"></span>
               @if (replyingTo() === comment.id) {
                  <button
                     (click)="cancelReply()"
                     class="flex items-center gap-1 text-primary font-medium"
                     matButton
                  >
                     <fa-icon
                        [icon]="['far', 'xmark']"
                        class="text-xs"
                     />
                     {{ 'general.cancel' | translate }}
                  </button>
               } @else if (editingCommentId() !== comment.id) {
                  <button
                     *webvisAclRestrict="commentRestriction"
                     (click)="startReply(comment.id)"
                     class="flex items-center gap-1 text-primary font-medium"
                     matButton
                  >
                     <fa-icon
                        [icon]="['far', 'reply']"
                        class="text-xs"
                     />
                     {{ 'approval.reply' | translate }}
                  </button>
                  @if (isOwnComment(comment)) {
                     <button
                        *webvisAclRestrict="commentRestriction"
                        [matMenuTriggerData]="{ comment: comment }"
                        [matMenuTriggerFor]="commentActionsMenu"
                        class="ml-1 text-on-surface-variant"
                        mat-icon-button
                     >
                        <fa-icon [icon]="['far', 'ellipsis-vertical']" />
                     </button>
                  }
               }
            }
         </div>

         @if (replyingTo() === comment.id) {
            <div
               [ngClass]="{ 'ml-13': !isChild }"
               class="flex flex-col gap-2 py-2 col-span-full"
            >
               <shared-ui-text-input
                  #replyInput
                  (keydown)="onCtrlEnter($event, 'reply', comment.id)"
                  (keydown.escape)="cancelReply()"
                  [inputFormControl]="replyForm"
                  [placeholder]="'approval.feedback_placeholder' | translate"
                  class="flex-1"
                  type="multi"
               />
               <button
                  (click)="submitReply(comment.id)"
                  [disabled]="replyForm.invalid"
                  mat-flat-button
               >
                  {{ 'approval.submit_comment' | translate }}
               </button>
            </div>
         }

         @if (loading() && loadingReplyTo() === comment.id) {
            <div
               [ngClass]="isChild ? '-ml-1' : 'ml-12'"
               class="col-span-full"
            >
               <shared-ui-skeleton-loader
                  [skeletonConfig]="{ appearance: { rows: 1, avatar: true, multiline: 3, divider: false }, styles: { width: '100%' } }"
                  [type]="skeletonType"
               />
            </div>
         }
      </div>
   }
</ng-template>

<mat-menu #commentActionsMenu="matMenu">
   <ng-template
      let-comment="comment"
      matMenuContent
   >
      <button
         (click)="startEdit(comment)"
         mat-menu-item
      >
         <fa-icon [icon]="['far', 'pencil']" />
         <span class="ml-2">{{ 'general.edit' | translate }}</span>
      </button>
      <button
         (click)="requestDelete(comment.id)"
         mat-menu-item
      >
         <fa-icon [icon]="['far', 'trash-alt']" />
         <span class="ml-2">{{ 'general.delete' | translate }}</span>
      </button>
   </ng-template>
</mat-menu>
